import React from 'react';
import '../../styles/components/AsperaLoginPage.scss';
import { AsperaHeader } from '../AsperaHeader';
import { AsperaWrapper } from '../AsperaWrapper';
import { Theme } from '../Carbon';
import { AsperaCustomTheming } from '../AsperaCustomTheming';
import { SubManager } from '../../classes/SubManager';
import { tracking } from '../../services/TrackingService';
import { AsperaError } from '../../classes/AsperaError';
import { withRouter } from 'react-router-dom';
import { createImageUrl } from '../../helpers/starterHelpers';
import { ImageLoginFlow } from '../../svg/ImageLoginFlow';
import { debounceTime, fromEvent } from 'rxjs';
import { modal } from '../../services/ModalService';
import { AsperaMarkdownRender } from '../AsperaMarkdownRender';
/**
 * React Component
 *
 * The AsperaLoginCustomForm renders login page wrapper.
 * Do not load this under AsperaAppWrapper. This should be a top level component.
 *
 * NOTES: Under the hood PUBLIC app is used to disable clickable events to exit.
 *
 * @example
 * ```javascript
 * <AsperaLoginPage />
 * ```
 */
export const AsperaLoginPage = withRouter(class AsperaLoginPage extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: false,
            error: false,
        };
        this.subManager = new SubManager();
    }
    get hasAnnouncement1() {
        const { loginData } = this.props;
        return !!(loginData.announcementOneTitle || loginData.announcementOneMessage);
    }
    get hasAnnouncement2() {
        const { loginData } = this.props;
        return !!(loginData.announcementTwoTitle || loginData.announcementTwoMessage);
    }
    get floatingAnnouncement() {
        const { loginData } = this.props;
        const baseAnnouncement = this.getAnnouncement(loginData.announcementTwoTitle, loginData.announcementTwoMessage, 'login-announcement');
        if (loginData.layout === 'CENTER') {
            return baseAnnouncement;
        }
        return React.createElement("div", { className: "floating-announcement-left" }, baseAnnouncement);
    }
    /**
     * Panel size is 26rem (416px).
     *
     * If announcement 2 is present we need to go stacked quicker.
     * Announcement min width is 20rem (320px)
     * Extra 20 is added to avoid clashing with the screen
     */
    get mobileMode() {
        const pageSize = window.innerWidth;
        return pageSize < ((this.hasAnnouncement2 ? 736 : 416) + 20);
    }
    getAnnouncement(title, message, className) {
        return (React.createElement("div", { className: `announcement-panels ${className ? className : ''}` },
            !!title && React.createElement("div", { className: "announcement-panels--title" }, title),
            !!message && React.createElement(AsperaMarkdownRender, { className: "announcement-panels--message", markdown: message })));
    }
    componentDidMount() {
        var _a;
        (_a = document.getElementById('aspera-offline-loading')) === null || _a === void 0 ? void 0 : _a.remove();
        this.subManager.setSubscription(fromEvent(window, 'resize').pipe(debounceTime(500)).subscribe({
            next: () => {
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaLoginPage: cannot setup resize event on window', error);
            },
        }));
        this.subManager.setSubscription(tracking.themeChange.subscribe({
            next: theme => {
                const body = document.body;
                if (body) {
                    body.classList.remove('cds--g100', 'cds--g10');
                    body.classList.add(`cds--${theme}`);
                }
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaAppWrapper: theme change subscription unable to load', error);
            },
        }));
        this.subManager.setSubscription(modal.modalNotifier.subscribe({
            next: () => {
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaLoginPage: Unable to monitor modals', error);
            },
        }));
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { loginData, className, children, productName, headerData, externalError, externalLoading, pageHeader, footerString, externalErrorProps, overrideContent } = this.props;
        const { loading, error } = this.state;
        const backgroundImage = createImageUrl(loginData.loginPageBackground);
        const pageLogo = createImageUrl(loginData.loginPageLogo);
        const hasFooter = !!footerString;
        const hasOverrideContent = !!overrideContent;
        return React.createElement("div", { className: `aspera-app-wrapper aspera-login-page ${className ? className : ''} ${hasOverrideContent ? 'override-content' : ''}` },
            React.createElement(Theme, { theme: "g100" },
                React.createElement(AsperaHeader, { productName: productName, currentApp: { id: 'public', displayName: 'PUBLIC', route: '/' }, currentUser: { isIbmIdUser: false, displayName: 'PUBLIC', displayUserRole: 'user' }, appOptions: [], helpOptions: [], brandingOptions: headerData })),
            React.createElement(AsperaCustomTheming, null),
            React.createElement(AsperaWrapper, { className: `login-page-content-wrapper ${this.mobileMode ? 'mobile-login' : ''} ${loginData.layout === 'CENTER' ? 'center-login-content' : 'left-login-content'}`, loading: externalLoading || loading, error: externalError || error, errorProps: externalError ? externalErrorProps : undefined, disableWrapping: true }, hasOverrideContent ? overrideContent : (React.createElement(React.Fragment, null,
                backgroundImage ? React.createElement("div", { className: `background-image ${hasFooter ? 'has-footer' : ''}`, style: { backgroundImage: `url(${backgroundImage})`, backgroundPosition: loginData.loginPageBackgroundPosition } }) : React.createElement("div", { className: "backup-background" },
                    React.createElement(ImageLoginFlow, null)),
                React.createElement("div", { className: "login-panel-wrapper" },
                    !!pageLogo && React.createElement("img", { className: "page-logo", src: pageLogo, alt: `${productName} - ${pageHeader}`, title: `${productName} - ${pageHeader}` }),
                    React.createElement("div", { className: "page-title" }, pageHeader),
                    React.createElement("div", { className: "children-wrapper" }, children),
                    this.hasAnnouncement1 && this.getAnnouncement(loginData.announcementOneTitle, loginData.announcementOneMessage, 'login-panel-announcement'),
                    (this.hasAnnouncement2 && this.mobileMode) && this.getAnnouncement(loginData.announcementTwoTitle, loginData.announcementTwoMessage, 'login-panel-announcement')),
                (this.hasAnnouncement2 && !this.mobileMode) && this.floatingAnnouncement,
                hasFooter && React.createElement("div", { className: "footer-content" }, footerString)))),
            React.createElement("div", { className: "modal-wrapper" }, modal.modalsToRender.map(item => item)));
    }
});
